// @section 1 Campaigns / Social — social media presence + post tracking.
// Placeholder today; activates when the social posting workflow ships.
//
// Channel inventory per memory.reference_social_launch_pack + memory.reference_cloudflare_domains:
//   - LinkedIn Company Pages: Vertis Group, NaviPoint Technologies, SignalLab, Conversiv (4 pages)
//   - X / Twitter: TBD
//   - Meta (FB + IG): TBD
//   - YouTube: TBD
// Outbound posting will route through the Bridge Workspace SA when we wire it.

import type { ReactNode } from 'react';

type Channel = {
  key: string;
  label: string;
  brand: string;
  status: 'live' | 'planned' | 'idle';
};

const CHANNELS: Channel[] = [
  { key: 'li-vertis-group',     label: 'LinkedIn · Vertis Group',          brand: 'Vertis Group',     status: 'live' },
  { key: 'li-navipoint',        label: 'LinkedIn · NaviPoint Technologies',brand: 'NaviPoint',        status: 'live' },
  { key: 'li-signal-rev',       label: 'LinkedIn · SignalLab / Signal Rev',brand: 'SignalLab',        status: 'live' },
  { key: 'li-conversiv',        label: 'LinkedIn · Conversiv',             brand: 'Conversiv',        status: 'live' },
  { key: 'li-vertis-holdings',  label: 'LinkedIn · Vertis Holdings',       brand: 'Vertis Holdings',  status: 'planned' },
  { key: 'x-vertis',            label: 'X · Vertis Group',                 brand: 'Vertis Group',     status: 'planned' },
  { key: 'meta-vertis',         label: 'Meta · Vertis (FB+IG)',            brand: 'Vertis Group',     status: 'planned' },
  { key: 'youtube-vertis',      label: 'YouTube · Vertis',                 brand: 'Vertis Group',     status: 'planned' },
];

export default function Social() {
  return (
    <div className="v5-page">
      <div className="page-head">
        <div className="crumb"><b>Atlas</b><span className="sep">›</span><span>Campaigns</span><span className="sep">›</span><b>Social</b></div>
        <div className="page-title">
          <h1>Social — Channel Tracking</h1>
          <span className="sub">Followers · impressions · engagement · post performance across all owned channels.</span>
        </div>
      </div>

      <div className="proto-detail-body">
        <div className="proto-detail-grid">
          <Module slug="social-roster" title="Channel roster" span={2}>
            <table className="proto-table">
              <thead><tr><th>Channel</th><th>Brand</th><th>Status</th></tr></thead>
              <tbody>
                {CHANNELS.map((c) => (
                  <tr key={c.key}>
                    <td>{c.label}</td>
                    <td className="muted">{c.brand}</td>
                    <td>
                      <span className={`conf conf-${c.status === 'live' ? 'high' : c.status === 'planned' ? 'medium' : 'failed'}`}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Module>

          <Module slug="social-followers" title="Follower growth">
            <Sketch>
              Daily follower count per channel + delta. LinkedIn Company API
              for the 4 live LinkedIn pages; Meta Graph for FB/IG when wired.
            </Sketch>
          </Module>

          <Module slug="social-post-queue" title="Scheduled posts">
            <Sketch>
              Upcoming + drafts. Each row: channel, copy, media,
              scheduled_for, status. Calendar view toggle.
            </Sketch>
          </Module>

          <Module slug="social-post-performance" title="Post performance">
            <Sketch>
              Per-post impressions · reactions · comments · click-throughs.
              Filter by channel · brand · campaign tag. Best/worst surfaces.
            </Sketch>
          </Module>

          <Module slug="social-engagement-inbox" title="Engagement inbox">
            <Sketch>
              New comments + DMs across channels in one queue. Reply-back routes
              through the appropriate platform API.
            </Sketch>
          </Module>

          <Module slug="social-content-calendar" title="Content calendar">
            <Sketch>
              Month view of all scheduled + published posts. Color-coded by
              brand + campaign. Drag to reschedule.
            </Sketch>
          </Module>

          <Module slug="social-cross-promo-attribution" title="Cross-promo attribution">
            <Sketch>
              Click-through from social posts → site landing (GA4 join via UTMs).
              Which post drove the most pipeline?
            </Sketch>
          </Module>
        </div>
      </div>
    </div>
  );
}

function Module({ slug, title, span, children }: { slug: string; title: string; span?: 1 | 2; children: ReactNode }) {
  return (
    <section className={`proto-module${span === 2 ? ' span-2' : ''}`}>
      <header>
        <h3>{title}</h3>
        <span className="mod-tag">module: <code>{slug}</code></span>
      </header>
      <div className="proto-module-body">{children}</div>
    </section>
  );
}

function Sketch({ children }: { children: ReactNode }) {
  return (
    <div style={{
      border: '1px dashed rgba(255,255,255,0.10)',
      borderRadius: 8,
      padding: 14,
      fontSize: 12,
      color: 'var(--tx-3)',
      lineHeight: 1.55,
    }}>
      {children}
    </div>
  );
}
